import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useError } from './ErrorBoundary';
import { isLoggedIn, login, logout, setLocale } from '../api';

const UserContext = React.createContext(null);
const EMPTY_STATE = {
	loggedIn: null,
	user: null
};
const LANGUAGE_LENGTH = 2;

export default function UserState({ children }) {
	const { i18n } = useTranslation();
	const setError = useError();
	const userState = useState(EMPTY_STATE);
	useEffect(() => {
		isLoggedIn().then(userState[1]).catch(setError);
	}, []);
	const lang = userState[0]?.user?.locale;
	useEffect(() => {
		if(lang) {
			i18n.changeLanguage(lang.slice(0, LANGUAGE_LENGTH));
		}
	}, [lang]);
	return <UserContext.Provider value={userState} children={children} />;
}

export function useUser() {
	return useContext(UserContext)[0];
}

export function useLogin(setError = useError()) {
	const setUser = useContext(UserContext)[1];
	return force => login(force).then(setUser).catch(setError);
}

export function useLogout() {
	const setUser = useContext(UserContext)[1];
	const setError = useError();
	return () => logout().then(setUser).catch(setError);
}

function getLocaleFromNavigator(prefix) {
	if(window.navigator.languages?.length) {
		const p = `${prefix}-`;
		for(const locale of window.navigator.languages) {
			if(locale === prefix || locale.startsWith(p)) {
				return locale;
			}
		}
	}
	return prefix;
}

export function useLocale() {
	const { user } = useUser();
	const { i18n } = useTranslation();
	return useMemo(() => {
		if(user?.locale) {
			return user.locale;
		}
		return getLocaleFromNavigator(i18n.language);
	}, [user?.locale, i18n.language]);
}

export function useSetLanguage() {
	const { i18n } = useTranslation();
	const [{ user }, setUser] = useContext(UserContext);
	return async language => {
		i18n.changeLanguage(language);
		if(user) {
			const locale = getLocaleFromNavigator(language);
			setUser(prev => {
				if(prev.user) {
					return { ...prev, user: { ...prev.user, locale } };
				}
				return prev;
			});
			await setLocale(locale);
		}
	};
}
